import { ALL_PERMISSIONS, PERMISSION_DESCRIPTIONS, type Permission } from '@aid/shared';

const ROLE_PERMISSION_CATEGORY = {
  OPERATION: 'operation',
  ADMINISTRATION: 'administration',
  SECURITY: 'security',
  OTHER: 'other',
} as const;

export type RolePermissionCategory =
  (typeof ROLE_PERMISSION_CATEGORY)[keyof typeof ROLE_PERMISSION_CATEGORY];

export interface RolePermissionModuleMetadata {
  resource: string;
  category: RolePermissionCategory;
  labelKey: string;
  descriptionKey?: string;
}

export interface PermissionParts {
  resource: string;
  action: string;
}

export interface PermissionCatalogItem {
  id: string;
  name: string;
}

export interface PermissionSummary {
  permissionCount: number;
  moduleCount: number;
}

export const ROLE_PERMISSION_CATEGORIES: readonly RolePermissionCategory[] = [
  ROLE_PERMISSION_CATEGORY.OPERATION,
  ROLE_PERMISSION_CATEGORY.ADMINISTRATION,
  ROLE_PERMISSION_CATEGORY.SECURITY,
  ROLE_PERMISSION_CATEGORY.OTHER,
];

export const ROLE_PERMISSION_CATEGORY_LABEL_KEYS: Record<RolePermissionCategory, string> = {
  [ROLE_PERMISSION_CATEGORY.OPERATION]: 'pages.roles.permissionCategories.operation',
  [ROLE_PERMISSION_CATEGORY.ADMINISTRATION]: 'pages.roles.permissionCategories.administration',
  [ROLE_PERMISSION_CATEGORY.SECURITY]: 'pages.roles.permissionCategories.security',
  [ROLE_PERMISSION_CATEGORY.OTHER]: 'pages.roles.permissionCategories.other',
};

/**
 * Presentation metadata intentionally lives beside the role feature. The
 * shared permission catalogue remains the source of truth for permission
 * values and their domain labels.
 */
export const ROLE_PERMISSION_MODULES: readonly RolePermissionModuleMetadata[] = [
  {
    resource: 'dashboard',
    category: ROLE_PERMISSION_CATEGORY.OPERATION,
    labelKey: 'pages.roles.modules.dashboard.label',
  },
  {
    resource: 'clientes',
    category: ROLE_PERMISSION_CATEGORY.OPERATION,
    labelKey: 'pages.roles.modules.clientes.label',
  },
  {
    resource: 'estado-cuenta',
    category: ROLE_PERMISSION_CATEGORY.OPERATION,
    labelKey: 'pages.roles.modules.estadoCuenta.label',
  },
  {
    resource: 'servicios',
    category: ROLE_PERMISSION_CATEGORY.OPERATION,
    labelKey: 'pages.roles.modules.servicios.label',
  },
  {
    resource: 'cobros',
    category: ROLE_PERMISSION_CATEGORY.OPERATION,
    labelKey: 'pages.roles.modules.cobros.label',
    descriptionKey: 'pages.roles.modules.cobros.description',
  },
  {
    resource: 'pagos',
    category: ROLE_PERMISSION_CATEGORY.OPERATION,
    labelKey: 'pages.roles.modules.pagos.label',
  },
  {
    resource: 'excepciones',
    category: ROLE_PERMISSION_CATEGORY.OPERATION,
    labelKey: 'pages.roles.modules.excepciones.label',
  },
  {
    resource: 'visitas',
    category: ROLE_PERMISSION_CATEGORY.OPERATION,
    labelKey: 'pages.roles.modules.visitas.label',
  },
  {
    resource: 'cobrador',
    category: ROLE_PERMISSION_CATEGORY.OPERATION,
    labelKey: 'pages.roles.modules.cobrador.label',
  },
  {
    resource: 'catalogos',
    category: ROLE_PERMISSION_CATEGORY.ADMINISTRATION,
    labelKey: 'pages.roles.modules.catalogos.label',
  },
  {
    resource: 'planes',
    category: ROLE_PERMISSION_CATEGORY.ADMINISTRATION,
    labelKey: 'pages.roles.modules.planes.label',
  },
  {
    resource: 'tipos-servicio',
    category: ROLE_PERMISSION_CATEGORY.ADMINISTRATION,
    labelKey: 'pages.roles.modules.tiposServicio.label',
  },
  {
    resource: 'comisiones',
    category: ROLE_PERMISSION_CATEGORY.ADMINISTRATION,
    labelKey: 'pages.roles.modules.comisiones.label',
  },
  {
    resource: 'users',
    category: ROLE_PERMISSION_CATEGORY.SECURITY,
    labelKey: 'pages.roles.modules.users.label',
    descriptionKey: 'pages.roles.modules.users.description',
  },
  {
    resource: 'roles',
    category: ROLE_PERMISSION_CATEGORY.SECURITY,
    labelKey: 'pages.roles.modules.roles.label',
    descriptionKey: 'pages.roles.modules.roles.description',
  },
  {
    resource: 'solicitudes',
    category: ROLE_PERMISSION_CATEGORY.SECURITY,
    labelKey: 'pages.roles.modules.solicitudes.label',
    descriptionKey: 'pages.roles.modules.solicitudes.description',
  },
];

const moduleMetadataByResource = new Map<string, RolePermissionModuleMetadata>(
  ROLE_PERMISSION_MODULES.map((module) => [module.resource, module]),
);

const categoryOrder = new Map<RolePermissionCategory, number>(
  ROLE_PERMISSION_CATEGORIES.map((category, index) => [category, index]),
);

const moduleOrder = new Map<string, number>(
  ROLE_PERMISSION_MODULES.map((module, index) => [module.resource, index]),
);

const permissionOrder = new Map<Permission, number>(
  ALL_PERMISSIONS.map((permission, index) => [permission, index]),
);

const FALLBACK_PERMISSION_ACTION_KEYS: Record<string, string> = {
  read: 'pages.roles.permissionActions.read',
  create: 'pages.roles.permissionActions.create',
  update: 'pages.roles.permissionActions.update',
  delete: 'pages.roles.permissionActions.delete',
  approve: 'pages.roles.permissionActions.approve',
  reject: 'pages.roles.permissionActions.reject',
  generar: 'pages.roles.permissionActions.generate',
  generate: 'pages.roles.permissionActions.generate',
  anular: 'pages.roles.permissionActions.annul',
  annul: 'pages.roles.permissionActions.annul',
};

const DESTRUCTIVE_ACTIONS = {
  DELETE: 'delete',
  ANNUL: 'annul',
  ANULAR: 'anular',
  REMOVE: 'remove',
  REVOKE: 'revoke',
} as const;

const destructiveActionValues: readonly string[] = Object.values(DESTRUCTIVE_ACTIONS);

export function splitPermissionName(name: string): PermissionParts {
  const segments = name.split(':');
  if (segments.length <= 1) {
    return { resource: name, action: name };
  }

  const action = segments[segments.length - 1] ?? name;
  return { resource: segments.slice(0, -1).join(':'), action };
}

export function getPermissionModuleMetadata(
  resource: string,
): RolePermissionModuleMetadata | undefined {
  return moduleMetadataByResource.get(resource);
}

export function getPermissionModuleCategory(resource: string): RolePermissionCategory {
  return getPermissionModuleMetadata(resource)?.category ?? ROLE_PERMISSION_CATEGORY.OTHER;
}

export function getPermissionModuleSortRank(resource: string): number {
  const categoryRank = categoryOrder.get(getPermissionModuleCategory(resource)) ?? 0;
  const knownModuleRank = moduleOrder.get(resource);
  const moduleRank = knownModuleRank ?? ROLE_PERMISSION_MODULES.length + 1;
  return categoryRank * 10_000 + moduleRank;
}

export function getPermissionSortRank(name: string): number {
  return permissionOrder.get(name as Permission) ?? Number.MAX_SAFE_INTEGER;
}

export function isKnownPermission(name: string): name is Permission {
  return Object.prototype.hasOwnProperty.call(PERMISSION_DESCRIPTIONS, name);
}

export function getCanonicalPermissionLabel(name: string): string | undefined {
  return isKnownPermission(name) ? PERMISSION_DESCRIPTIONS[name] : undefined;
}

export function getFallbackPermissionActionKey(action: string): string | undefined {
  return FALLBACK_PERMISSION_ACTION_KEYS[action.toLowerCase()];
}

export function humanizePermissionPart(value: string): string {
  const words = value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(/[-_\s]+/)
    .filter(Boolean);

  if (words.length === 0) return value;

  return words
    .map((word, index) => {
      const lowerWord = word.toLocaleLowerCase();
      return index === 0 ? lowerWord.charAt(0).toLocaleUpperCase() + lowerWord.slice(1) : lowerWord;
    })
    .join(' ');
}

export function getPermissionModuleAnchorId(resource: string): string {
  const normalized = resource
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return `permission-module-${normalized || 'other'}`;
}

export function isDestructivePermissionAction(action: string): boolean {
  return destructiveActionValues.includes(action.toLocaleLowerCase());
}

export function summarizePermissionNames(names: readonly string[]): PermissionSummary {
  const uniqueNames = new Set(names);
  const resources = new Set<string>();

  for (const name of uniqueNames) {
    resources.add(splitPermissionName(name).resource);
  }

  return {
    permissionCount: uniqueNames.size,
    moduleCount: resources.size,
  };
}

export function summarizeSelectedPermissions(
  selectedIds: readonly string[],
  permissions: readonly PermissionCatalogItem[],
): PermissionSummary {
  const selected = new Set(selectedIds);
  const selectedNames = permissions
    .filter((permission) => selected.has(permission.id))
    .map((p) => p.name);
  return summarizePermissionNames(selectedNames);
}
